import { observer } from 'mobx-react-lite';
import type { ApplicationSdk } from '../../../../core/sdk';
import { useEffect } from 'react';
import { TrainingStore } from '../';
import { Button } from 'primereact/button';
import { Message } from 'primereact/message';
import { ProgressSpinner } from 'primereact/progressspinner';

const getBorderColor = (isTraining: boolean, isCompleted: boolean): string => {
    if (isTraining) {
        return isCompleted ? '2px solid #4CAF50' : '2px solid #9ca3af';
    }
    return '1px solid #d1d5db';
};

const getPreStartHintText = (training: any): string | null => {
    if (!training || training.completed) return null;

    switch (training.contentType) {
        case 'video':
            return 'Schau dir bitte erst das Video an';
        case 'pdf':
            return 'Lese dir bitte erst die PDF durch';
        default:
            return null;
    }
};

const TrainingViewFinish = observer(({ store, failed }: { store: any; failed: any }) => {
    const bgColor = failed ? '#f8d7da' : '#d4edda'; // red for failed, green for success
    const textColor = failed ? '#721c24' : '#155724';
    const icon = failed ? 'pi-times-circle' : 'pi-check-circle';

    return (
        <div
            style={{
                position: 'fixed',
                top: '50%',
                left: '50%',
                transform: 'translate(-50%, -50%)',
                backgroundColor: bgColor,
                color: textColor,
                padding: '2rem',
                borderRadius: '8px',
                boxShadow: '0 4px 8px rgba(0, 0, 0, 0.2)',
                textAlign: 'center',
                zIndex: 1000,
                width: '80%',
                maxWidth: '500px',
            }}
        >
            <i
                className={`pi ${icon}`}
                style={{ fontSize: '2rem', marginBottom: '1rem', display: 'block' }}
            ></i>
            <h2 style={{ margin: 0, fontSize: '1.5rem' }}>
                {failed ? (
                    <>Schulung nicht bestanden. Versuchen Sie es erneut! </>
                ) : (
                    <> Schulung erfolgreich abgeschlossen!</>
                )}
            </h2>
            <Button
                label="Zurück"
                onClick={() => store.quitTraining()}
                style={{
                    marginTop: '1rem',
                    backgroundColor: '#ff6f61',
                    color: '#fff',
                    padding: '10px 20px',
                    fontSize: '16px',
                    borderRadius: '5px',
                }}
            />
        </div>
    );
});

const TrainingViewQuestion = observer(({ store }: { store: any }) => {
    const useTraining = store?.currentTraining || null;
    const useSession = useTraining.session || null;
    const isFinished = useSession.question.finished;
    const isQuestionCorrect = useSession.question.correct === true;

    return (
        <div style={{ width: '100%' }}>
            <h2 style={{ marginBottom: '20px', fontSize: '20px', fontWeight: 'bold' }}>
                {useTraining.session.question.question}
            </h2>
            {useTraining.session.question.multiple && <i>Mehrere Antworten möglich</i>}
            {isFinished && (
                <div style={{ marginBottom: '20px' }}>
                    {useSession.question.correct ? (
                        <Message
                            severity="success"
                            text="Ihre Antwort ist richtig"
                            icon="pi pi-check-circle"
                            style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}
                        />
                    ) : (
                        <Message
                            severity="error"
                            text="Ihre Antwort ist falsch"
                            icon="pi pi-times-circle"
                            style={{ fontSize: '16px', display: 'flex', alignItems: 'center' }}
                        />
                    )}
                </div>
            )}
            {useTraining.session.question.answers.map((answer: any, index: number) => {
                const isCorrect =
                    (useSession.question.correct === true ||
                        useSession.question.correct === false) &&
                    (useTraining.session.question.correctAnswers || []).includes(answer.answer_id);
                const isWrong =
                    (useSession.question.correct === true ||
                        useSession.question.correct === false) &&
                    !(useTraining.session.question.correctAnswers || []).includes(answer.answer_id);
                const isSelected = store.currentAnswer.includes(answer.answer_id);

                let useBackgroundColor = '#fff';
                let useTextColor = '#000';

                if (isCorrect) {
                    useBackgroundColor = '#4CAF50';
                    useTextColor = '#fff';
                } else if (isWrong && isSelected) {
                    useBackgroundColor = '#dc3545';
                    useTextColor = '#fff';
                } else if (isSelected) {
                    useBackgroundColor = '#007bff';
                    useTextColor = '#fff';
                }

                return (
                    <div
                        key={`answer-${index}`}
                        onClick={() => store.selectAnswer(answer.answer_id)}
                        style={{
                            width: '100%',
                            display: 'flex',
                            alignItems: 'center',
                            padding: '15px',
                            marginBottom: '10px',
                            border: '1px solid #ccc',
                            borderRadius: '5px',
                            backgroundColor: useBackgroundColor,
                            color: useTextColor,
                            cursor: 'pointer',
                        }}
                    >
                        {isCorrect && (
                            <i
                                className="pi pi-check-circle"
                                style={{ float: 'left', marginRight: '10px', color: useTextColor }}
                            ></i>
                        )}
                        {isWrong && (
                            <i
                                className="pi pi-times-circle"
                                style={{ float: 'left', marginRight: '10px', color: useTextColor }}
                            ></i>
                        )}
                        <span>{answer.answer}</span>
                    </div>
                );
            })}
            {isFinished && (
                <Button
                    label={isQuestionCorrect ? 'Nächste Frage' : 'Weiter'}
                    onClick={() => {
                        isQuestionCorrect ? store.nextQuestion() : store.trainingFinishFailed();
                    }}
                    style={{
                        width: '100%',
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        padding: '15px',
                        borderRadius: '5px',
                        marginTop: '20px',
                    }}
                />
            )}
            {!isFinished && (
                <Button
                    label="Antwort senden"
                    onClick={() => store.answerQuestion(store.currentAnswer)}
                    disabled={store.currentAnswer.length === 0}
                    style={{
                        width: '100%',
                        backgroundColor: '#4CAF50',
                        color: '#fff',
                        padding: '15px',
                        fontSize: '16px',
                        borderRadius: '5px',
                        marginTop: '20px',
                    }}
                />
            )}
        </div>
    );
});

const TrainingViewPdfPreview = observer(({ store, training }: { store: any; training: any }) => {
    return (
        <div
            style={{ textAlign: 'center', cursor: 'pointer' }}
            onClick={() => store.setPdfFullscreenMode(true)}
        >
            <div
                style={{
                    width: '100%',
                    height: '300px',
                    backgroundColor: '#f5f5f5',
                    border: '2px dashed #ccc',
                    borderRadius: '10px',
                    display: 'flex',
                    flexDirection: 'column',
                    alignItems: 'center',
                    justifyContent: 'center',
                    marginBottom: '1rem',
                    transition: 'all 0.3s ease',
                }}
                onMouseOver={e => {
                    e.currentTarget.style.backgroundColor = '#e8f5e9';
                    e.currentTarget.style.borderColor = '#4CAF50';
                }}
                onMouseOut={e => {
                    e.currentTarget.style.backgroundColor = '#f5f5f5';
                    e.currentTarget.style.borderColor = '#ccc';
                }}
            >
                <i
                    className="pi pi-file-pdf"
                    style={{ fontSize: '4rem', color: '#d32f2f', marginBottom: '1rem' }}
                ></i>
                <h3 style={{ margin: 0, color: '#666', fontSize: '1.2rem' }}>PDF-Schulung</h3>
                <p style={{ margin: '0.5rem 0 0 0', color: '#888', fontSize: '1rem' }}>
                    Klicken Sie hier, um die PDF zu öffnen
                </p>
            </div>
        </div>
    );
});

const TrainingViewPdfFullscreen = observer(({ store, training }: { store: any; training: any }) => {
    // PDF.js Viewer URL - verwende den eingebauten Viewer
    const pdfViewerUrl = `/pdf2js/web/viewer.html?file=${encodeURIComponent(training.pdfUrl)}`;

    return (
        <div
            style={{
                position: 'fixed',
                top: '0',
                left: '0',
                width: '100vw',
                height: '100vh',
                backgroundColor: '#fff',
                display: 'flex',
                flexDirection: 'column',
                zIndex: 9999,
                WebkitTransform: 'translateZ(0)', // iOS Hardware-Beschleunigung
                transform: 'translateZ(0)',
                WebkitBackfaceVisibility: 'hidden', // iOS Rendering-Fix
                backfaceVisibility: 'hidden',
                isolation: 'isolate', // Neue Stacking-Context für ältere Browser
                WebkitPerspective: 1000, // iOS 3D-Rendering aktivieren
                perspective: 1000,
            }}
        >
            <div
                style={{
                    position: 'absolute',
                    top: '20px',
                    right: '20px',
                    zIndex: 10000,
                }}
            >
                <button
                    onClick={() => store.setPdfFullscreenMode(false)}
                    style={{
                        background: 'rgba(0, 0, 0, 0.7)',
                        color: 'white',
                        border: 'none',
                        borderRadius: '50%',
                        width: '40px',
                        height: '40px',
                        fontSize: '20px',
                        cursor: 'pointer',
                        display: 'flex',
                        alignItems: 'center',
                        justifyContent: 'center',
                        WebkitTransform: 'translateZ(0)',
                        transform: 'translateZ(0)',
                        WebkitBackfaceVisibility: 'hidden',
                        backfaceVisibility: 'hidden',
                    }}
                >
                    ×
                </button>
            </div>
            <div
                style={{
                    flex: 1,
                    padding: 0,
                    width: '100%',
                    height: '100%',
                    overflow: 'hidden', // PDF.js Viewer handhabt sein eigenes Scrolling
                }}
            >
                <iframe
                    src={pdfViewerUrl}
                    title={training.label}
                    style={{
                        width: '100%',
                        height: '100%',
                        border: 'none',
                    }}
                    allowFullScreen
                />
            </div>
        </div>
    );
});

const TrainingNavigation = observer(({ store }: { store: any }) => {
    const useTraining = store?.currentTraining || null;
    const preHint = getPreStartHintText(useTraining);

    const handleIframeLoad = () => {
        store.onContentLoaded();
    };

    return (
        <>
            {useTraining?.session ? (
                <>
                    {useTraining.session.finished ? (
                        <TrainingViewFinish
                            store={store}
                            failed={useTraining.session.failed}
                        />
                    ) : (
                        <TrainingViewQuestion store={store} />
                    )}
                </>
            ) : (
                <>
                    {store?.pdfFullscreenMode && useTraining?.contentType === 'pdf' ? (
                        <TrainingViewPdfFullscreen
                            store={store}
                            training={useTraining}
                        />
                    ) : (
                        <>
                            {useTraining.img && (
                                <img
                                    src={useTraining.img}
                                    alt="Training Bild"
                                    style={{
                                        width: '64px',
                                        height: '64px',
                                        borderRadius: '12px',
                                        objectFit: 'cover',
                                    }}
                                />
                            )}
                            <h2 style={{ fontSize: '1.5rem', margin: 0, fontWeight: 'bold' }}>
                                {useTraining.label}
                            </h2>
                            <p style={{ fontSize: '1rem', margin: 0, color: '#555' }}>
                                {useTraining.data}
                            </p>
                            {useTraining.contentType === 'video' && useTraining.video && (
                                <iframe
                                    width="100%"
                                    src={useTraining.video}
                                    title={useTraining.label}
                                    frameBorder="0"
                                    allow="accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                                    allowFullScreen
                                    style={{ borderRadius: '10px' }}
                                    onLoad={handleIframeLoad}
                                />
                            )}
                            {useTraining.contentType === 'pdf' && useTraining.pdfUrl && (
                                <TrainingViewPdfPreview
                                    store={store}
                                    training={useTraining}
                                />
                            )}
                            {!!useTraining.completed && (
                                <Message
                                    severity="info"
                                    text="Schulung wurde bereits erfolgreich abgeschlossen"
                                    icon="pi pi-check-circle"
                                    style={{
                                        marginTop: '1rem',
                                        fontSize: '16px',
                                        display: 'flex',
                                        alignItems: 'center',
                                        backgroundColor: '#e8f5e9',
                                        color: '#388e3c',
                                        border: '1px solid #c8e6c9',
                                        borderRadius: '5px',
                                        padding: '10px',
                                    }}
                                />
                            )}
                            {!useTraining.completed && (
                                <>
                                    {preHint ? (
                                        <div
                                            style={{
                                                color: '#333',
                                                fontSize: '13px',
                                                fontStyle: 'italic',
                                                textAlign: 'center',
                                                opacity: 0.9,
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                gap: '6px',
                                            }}
                                        >
                                            <i
                                                className="pi pi-info-circle"
                                                style={{ fontSize: '13px' }}
                                            ></i>
                                            {preHint}
                                        </div>
                                    ) : null}
                                    <Button
                                        label="Prüfung starten"
                                        onClick={() => store.startTraining()}
                                        disabled={!store.startButtonEnabled}
                                        style={{
                                            width: '100%',
                                            color: '#fff',
                                            padding: '12px',
                                            fontSize: '14px',
                                            borderRadius: '5px',
                                            margin: 0,
                                            opacity: store.startButtonEnabled ? 1 : 0.6,
                                        }}
                                    />
                                </>
                            )}
                        </>
                    )}
                </>
            )}
        </>
    );
});

const TrainingView = observer(({ store }: { store: any }) => {
    return (
        <div
            style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: '20px' }}
        >
            <TrainingNavigation store={store} />
        </div>
    );
});

const TrainingTreeComponent = observer(({ tree, store }: { store: TrainingStore; tree: any }) => {
    const handleClick = (node: any) => {
        if (node.type === 'training') {
            store.setCurrentTraining(node.id);
        } else if (node.type === 'folder') {
            store.setCurrentFolder(node.id);
        }
    };

    return (
        <ul style={{ listStyleType: 'none', padding: 0 }}>
            {tree.length === 0 ? (
                <li
                    style={{
                        textAlign: 'center',
                        color: '#6c757d',
                        marginTop: '3rem',
                        fontSize: '1.1rem',
                        padding: '2rem',
                    }}
                >
                    <i
                        className="pi pi-folder-open"
                        style={{
                            fontSize: '3rem',
                            marginBottom: '1rem',
                            display: 'block',
                            color: '#adb5bd',
                        }}
                    ></i>
                    Kein Inhalt in diesem Ordner
                </li>
            ) : (
                tree.map((node: any, index: any) => {
                    const isTraining = node.type === 'training';
                    const isFolder = node.type === 'folder';
                    const isCompleted = node.completed === 1;

                    return (
                        <li
                            key={index}
                            onClick={() => handleClick(node)}
                            style={{
                                display: 'flex',
                                alignItems: 'center',
                                marginBottom: '16px',
                                cursor: 'pointer',
                                padding: '16px',
                                border: getBorderColor(isTraining, isCompleted),
                                borderRadius: '12px',
                                backgroundColor: isTraining
                                    ? node.bgcolor || '#f0f9ff'
                                    : node.bgcolor || '#f8f9fa',
                                position: 'relative',
                                overflow: 'hidden',
                            }}
                        >
                            {node.img ? (
                                <img
                                    src={node.img}
                                    alt="Item Bild"
                                    style={{
                                        width: '60px',
                                        height: '60px',
                                        borderRadius: '12px',
                                        marginRight: '16px',
                                        objectFit: 'cover',
                                        flexShrink: 0,
                                    }}
                                />
                            ) : (
                                <div style={{ marginRight: '16px', flexShrink: 0 }}>
                                    {isTraining ? (
                                        <img
                                            src={'/schulung.svg'}
                                            alt="Schulung"
                                            style={{
                                                width: '32px',
                                                height: '32px',
                                            }}
                                        />
                                    ) : isFolder ? (
                                        <i
                                            className="pi pi-folder"
                                            style={{
                                                fontSize: '32px',
                                                color: '#2196F3',
                                            }}
                                        ></i>
                                    ) : null}
                                </div>
                            )}
                            <div style={{ flex: 1 }}>
                                <h3
                                    style={{
                                        margin: '0 0 8px 0',
                                        textAlign: 'left',
                                        fontSize: '18px',
                                        fontWeight: '600',
                                        color: '#2c3e50',
                                        display: 'flex',
                                        alignItems: 'center',
                                        gap: '8px',
                                    }}
                                >
                                    {node.label}
                                    {node.completed ? (
                                        <div
                                            style={{
                                                backgroundColor: '#4CAF50',
                                                borderRadius: '50%',
                                                width: '24px',
                                                height: '24px',
                                                display: 'flex',
                                                alignItems: 'center',
                                                justifyContent: 'center',
                                                flexShrink: 0,
                                            }}
                                        >
                                            <i
                                                className="pi pi-check"
                                                style={{
                                                    color: 'white',
                                                    fontSize: '12px',
                                                }}
                                                title="Abgeschlossen"
                                            />
                                        </div>
                                    ) : null}
                                </h3>
                                <p
                                    style={{
                                        margin: 0,
                                        textAlign: 'left',
                                        color: '#6c757d',
                                        fontSize: '14px',
                                        lineHeight: '1.5',
                                    }}
                                >
                                    {node.data ||
                                        (isTraining
                                            ? 'Klicken Sie hier, um die Schulung zu starten'
                                            : 'Klicken Sie hier, um den Ordner zu öffnen')}
                                </p>
                            </div>
                        </li>
                    );
                })
            )}
        </ul>
    );
});

const TrainingComponent = observer(({ sdk }: { sdk: ApplicationSdk }) => {
    useEffect(() => {
        sdk.useStore(TrainingStore);
    }, [sdk]);

    const store = sdk.currentStore as TrainingStore;
    const useTree = store?.currentTree || [];
    const useTraining = store?.currentTraining || null;

    return (
        <div
            style={{
                maxWidth: '600px',
                margin: '0 auto',
                padding: '8px',
                background: '#fff',
                minHeight: '80vh',
            }}
        >
            {store?.trainingListLoading ? (
                <div
                    style={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        justifyContent: 'center',
                        minHeight: '50vh',
                        gap: '16px',
                    }}
                >
                    <ProgressSpinner
                        style={{ width: '48px', height: '48px' }}
                        strokeWidth="3"
                        animationDuration="1s"
                    />
                    <span
                        style={{
                            fontSize: '16px',
                            color: '#6c757d',
                            textAlign: 'center',
                        }}
                    >
                        Trainingsliste wird geladen...
                    </span>
                </div>
            ) : useTraining ? (
                <TrainingView store={store} />
            ) : (
                <TrainingTreeComponent
                    store={store}
                    tree={useTree}
                />
            )}
        </div>
    );
});

export default TrainingComponent;
